import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Flag, Radio } from "lucide-react";

export default function EventDetailPage() {
  const { eventId } = useParams();
  const [ev, setEv] = useState(null);
  const [regs, setRegs] = useState([]);
  const [councils, setCouncils] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [openCouncil, setOpenCouncil] = useState(false);
  const [openCountry, setOpenCountry] = useState(false);
  const [openSession, setOpenSession] = useState(false);
  const [newCouncil, setNewCouncil] = useState({ name: "", topic: "", description: "" });
  const [newCountry, setNewCountry] = useState({ council_id: "", name: "" });
  const [newSession, setNewSession] = useState({ council_id: "", agenda: "", timer_seconds: 90 });

  const load = async () => {
    const [e, r, c, co, s] = await Promise.all([
      api.get(`/events/${eventId}`),
      api.get(`/registrations?event_id=${eventId}`),
      api.get(`/councils?event_id=${eventId}`),
      api.get(`/countries?event_id=${eventId}`),
      api.get(`/sessions?event_id=${eventId}`),
    ]);
    setEv(e.data);
    setRegs(r.data);
    setCouncils(c.data);
    setCountries(co.data);
    setSessions(s.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const allocate = async (reg, council_id, country_id) => {
    try {
      await api.post("/registrations/allocate", {
        registration_id: reg.id,
        council_id,
        country_id,
      });
      toast.success("Delegate allocated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const publish = async () => {
    try {
      await api.patch(`/events/${eventId}`, { status: "published" });
      toast.success("Event published");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const createCouncil = async (e) => {
    e.preventDefault();
    try {
      await api.post("/councils", { ...newCouncil, event_id: eventId });
      setOpenCouncil(false);
      setNewCouncil({ name: "", topic: "", description: "" });
      load();
      toast.success("Council added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const createCountry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/countries", { ...newCountry, event_id: eventId });
      setOpenCountry(false);
      setNewCountry({ council_id: "", name: "" });
      load();
      toast.success("Country added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const createSession = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sessions", { ...newSession, event_id: eventId, timer_seconds: Number(newSession.timer_seconds) });
      setOpenSession(false);
      setNewSession({ council_id: "", agenda: "", timer_seconds: 90 });
      load();
      toast.success("Session created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!ev) return <DashboardLayout><p>Loading…</p></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <Link to="/dashboard/events" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 hover:text-[#0F4C81]" data-testid="back-to-events-link">
          <ArrowLeft className="h-3.5 w-3.5" /> All events
        </Link>

        {/* Hero strip */}
        <div className="mt-5 rounded-2xl overflow-hidden bg-[#0A192F] relative">
          <div
            className="h-56 bg-cover bg-center opacity-40"
            style={{ backgroundImage: `url(${ev.banner_url})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] to-[#0A192F]/40" />
          <div className="absolute inset-0 p-8 flex items-end justify-between">
            <div className="text-white">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                {ev.format} · {ev.status}
              </p>
              <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter">{ev.title}</h1>
              <p className="mt-1 text-sm text-slate-300">
                {new Date(ev.start_date).toLocaleDateString()} — {new Date(ev.end_date).toLocaleDateString()} · {ev.location}
              </p>
            </div>
            <div className="hidden md:flex flex-col items-end gap-2">
              <Link to={`/events/${ev.slug}`} target="_blank">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="view-public-page-button">
                  View public page
                </Button>
              </Link>
              <Link to={`/dashboard/events/${ev.id}/roles`}>
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="event-role-setup-button">
                  Role Setup
                </Button>
              </Link>
              {ev.status !== "published" && (
                <Button onClick={publish} className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold" data-testid="publish-event-button">
                  Publish event
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniStat label="Registrations" value={regs.length} />
          <MiniStat label="Allocated" value={regs.filter((r) => r.allocated_country_id).length} />
          <MiniStat label="Councils" value={councils.length} />
          <MiniStat label="Countries" value={countries.length} />
        </div>

        <Tabs defaultValue="registrations" className="mt-10">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
            <TabsTrigger value="councils" data-testid="tab-councils">Councils & Countries</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
            <TabsTrigger value="meet" data-testid="tab-meet">Meet / Zoom</TabsTrigger>
          </TabsList>

          <TabsContent value="registrations" className="mt-6">
            <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto" data-testid="registrations-table-scroller">
              <table className="w-full text-sm min-w-[960px]">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">Delegate</th>
                    <th className="text-left px-5 py-3">Payment</th>
                    <th className="text-left px-5 py-3">Council</th>
                    <th className="text-left px-5 py-3">Country</th>
                    <th className="text-left px-5 py-3">Allocate</th>
                  </tr>
                </thead>
                <tbody>
                  {regs.map((r) => {
                    const councilCountries = countries.filter((c) => c.council_id === (r.allocated_council_id || ""));
                    return (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-5 py-3">
                          <p className="font-semibold text-[#0A192F]">{r.delegate_name}</p>
                          <p className="text-xs text-slate-500">{r.delegate_email}</p>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-[#D4AF37]/20 text-[#0A192F]"}`}>
                            {r.payment_status}
                          </span>
                        </td>
                        <td className="px-5 py-3 w-48">
                          <Select value={r.allocated_council_id || ""} onValueChange={(v) => setRegs((prev) => prev.map((x) => x.id === r.id ? { ...x, allocated_council_id: v, allocated_country_id: "" } : x))}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Select council" /></SelectTrigger>
                            <SelectContent>
                              {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-3 w-56">
                          <Select value={r.allocated_country_id || ""} onValueChange={(v) => setRegs((prev) => prev.map((x) => x.id === r.id ? { ...x, allocated_country_id: v } : x))}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Select country" /></SelectTrigger>
                            <SelectContent>
                              {councilCountries.map((c) => <SelectItem key={c.id} value={c.id} disabled={!!c.allocated_delegate_id && c.allocated_delegate_id !== r.delegate_id}>{c.name}{c.allocated_delegate_id ? " · taken" : ""}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-5 py-3">
                          <Button
                            size="sm"
                            disabled={!r.allocated_council_id || !r.allocated_country_id}
                            onClick={() => allocate(r, r.allocated_council_id, r.allocated_country_id)}
                            className="bg-[#0F4C81] hover:bg-[#0A3860] h-9"
                            data-testid={`allocate-${r.id}`}
                          >
                            Confirm
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {regs.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-slate-500">No registrations yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="councils" className="mt-6">
            <div className="flex items-center gap-3 flex-wrap">
              <Dialog open={openCouncil} onOpenChange={setOpenCouncil}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="add-council-button">
                    <Plus className="h-4 w-4 mr-1" /> Add council
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New council</DialogTitle></DialogHeader>
                  <form onSubmit={createCouncil} className="space-y-4" data-testid="council-form">
                    <div>
                      <Label>Name</Label>
                      <Input required value={newCouncil.name} onChange={(e) => setNewCouncil({ ...newCouncil, name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Topic / Agenda</Label>
                      <Input required value={newCouncil.topic} onChange={(e) => setNewCouncil({ ...newCouncil, topic: e.target.value })} />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={newCouncil.description} onChange={(e) => setNewCouncil({ ...newCouncil, description: e.target.value })} rows={3} />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Create</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              <Dialog open={openCountry} onOpenChange={setOpenCountry}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81]" data-testid="add-country-button">
                    <Plus className="h-4 w-4 mr-1" /> Add country
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New country / portfolio</DialogTitle></DialogHeader>
                  <form onSubmit={createCountry} className="space-y-4" data-testid="country-form">
                    <div>
                      <Label>Council</Label>
                      <Select value={newCountry.council_id} onValueChange={(v) => setNewCountry({ ...newCountry, council_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Choose council" /></SelectTrigger>
                        <SelectContent>
                          {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Country / Portfolio</Label>
                      <Input required value={newCountry.name} onChange={(e) => setNewCountry({ ...newCountry, name: e.target.value })} />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Add</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              {councils.map((c) => {
                const rows = countries.filter((x) => x.council_id === c.id);
                return (
                  <div key={c.id} className="rounded-xl bg-white border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">Council</p>
                        <h3 className="mt-1 text-lg font-semibold tracking-tight text-[#0A192F]">{c.name}</h3>
                        <p className="text-sm font-serif italic text-slate-500 mt-0.5">{c.topic}</p>
                      </div>
                      <Flag className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <div className="mt-5 divide-y divide-slate-100 max-h-64 overflow-auto">
                      {rows.map((r) => (
                        <div key={r.id} className="flex items-center justify-between py-2 text-sm">
                          <span className="text-[#0A192F]">{r.name}</span>
                          <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${r.allocated_delegate_id ? "text-emerald-600" : "text-slate-400"}`}>
                            {r.allocated_delegate_id ? "allocated" : "open"}
                          </span>
                        </div>
                      ))}
                      {rows.length === 0 && <p className="text-xs text-slate-500 py-4">No countries yet</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="sessions" className="mt-6">
            <Dialog open={openSession} onOpenChange={setOpenSession}>
              <DialogTrigger asChild>
                <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="add-session-button">
                  <Plus className="h-4 w-4 mr-1" /> Create session
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Open a new committee session</DialogTitle></DialogHeader>
                <form onSubmit={createSession} className="space-y-4" data-testid="session-form">
                  <div>
                    <Label>Council</Label>
                    <Select value={newSession.council_id} onValueChange={(v) => setNewSession({ ...newSession, council_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Choose council" /></SelectTrigger>
                      <SelectContent>
                        {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Agenda</Label>
                    <Input required value={newSession.agenda} onChange={(e) => setNewSession({ ...newSession, agenda: e.target.value })} />
                  </div>
                  <div>
                    <Label>Default speaking time (seconds)</Label>
                    <Input type="number" value={newSession.timer_seconds} onChange={(e) => setNewSession({ ...newSession, timer_seconds: e.target.value })} />
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Create</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              {sessions.map((s) => {
                const council = councils.find((c) => c.id === s.council_id);
                return (
                  <Link to={`/dashboard/live/${s.id}`} key={s.id} className="rounded-xl bg-[#0A192F] text-white p-6 border border-[#D4AF37]/20 hover:-translate-y-1 transition-all block" data-testid={`session-card-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                        {council?.name || "Council"}
                      </p>
                      <span className="flex items-center gap-1 text-xs font-mono">
                        <Radio className="h-3 w-3 text-emerald-400" /> {s.status}
                      </span>
                    </div>
                    <h3 className="mt-3 text-lg font-semibold">{s.agenda}</h3>
                    <p className="mt-2 text-xs text-slate-400">Default timer: {s.timer_seconds}s</p>
                  </Link>
                );
              })}
              {sessions.length === 0 && <p className="text-sm text-slate-500">No sessions yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="meet" className="mt-6">
            <div className="rounded-xl bg-white border border-slate-200 p-8">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Google Meet / Zoom placeholders</h3>
              <p className="text-sm text-slate-500 mt-2">Every session generates mock integration links. Connect real OAuth in production.</p>
              <div className="mt-6 space-y-3">
                {sessions.map((s) => (
                  <MeetRow key={s.id} sessionId={s.id} agenda={s.agenda} />
                ))}
                {sessions.length === 0 && <p className="text-sm text-slate-500">Create a session first.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function MeetRow({ sessionId, agenda }) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    api.get(`/meet/placeholder/${sessionId}`).then((r) => setLinks(r.data));
  }, [sessionId]);
  return (
    <div className="p-4 rounded-md border border-slate-200 flex items-center justify-between flex-wrap gap-3">
      <div>
        <p className="text-sm font-semibold text-[#0A192F]">{agenda}</p>
        <p className="text-xs text-slate-500 font-mono">{sessionId}</p>
      </div>
      <div className="flex gap-2">
        <a href={links?.google_meet_url} target="_blank" rel="noreferrer">
          <Button variant="outline" size="sm" className="text-xs uppercase tracking-[0.18em]">Google Meet</Button>
        </a>
        <a href={links?.zoom_url} target="_blank" rel="noreferrer">
          <Button variant="outline" size="sm" className="text-xs uppercase tracking-[0.18em]">Zoom</Button>
        </a>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <p className="mt-2 font-serif text-2xl text-[#0A192F]">{value}</p>
    </div>
  );
}
